// exp_PORTFOLIO-61587_file
import { FC } from 'react';

import { formatCost } from 'utils/clickme';
import { NON_BREAKING_SPACE, SPACE } from 'utils/constants/common';
import {
    budgetFromClicks,
    CLICKS_INPUT_STEP,
    clicksFromBudget,
    getVacancyOfTheDayForecast,
} from 'utils/vacancyOfTheDay';

import vacancyOfTheDayFormFieldTouch from '@hh.ru/analytics-js-events/build/clickme/employer/campaign_menu/vacancy_of_the_day/vacancy_of_the_day_form_field_touch';
import { Button, Flex, NumberInput, Text, useBreakpoint, VSpacing } from '@hh.ru/magritte-ui';
import { MinusOutlinedSize24, PlusOutlinedSize24 } from '@hh.ru/magritte-ui/icon';

import useTrls from 'hooks/translation/useTrls';

import { useVacancyOfTheDayAnalyticsParams } from 'components/VacancyOfTheDayBuilder/useVacancyOfTheDayAnalyticsParams';

import {
    selectVacancyOfTheDayBudget,
    selectVacancyOfTheDayClicks,
    selectVacancyOfTheDayErrors,
    selectVacancyOfTheDayPresets,
    setVacancyOfTheDayBudget,
    setVacancyOfTheDayClicks,
    setVacancyOfTheDayErrors,
} from 'store/models/vacancyOfTheDay';
import { useDispatch, useSelector } from 'store/utils';

const TrlKeys = {
    clicks: 'VacancyOfTheDayPage.clicks.title',
    clicksError: 'VacancyOfTheDayPage.clicks.error',
    budget: 'VacancyOfTheDayPage.budget.title',
    budgetError: 'VacancyOfTheDayPage.budget.error',
    postfix: 'currency.short',
};

export const CustomClicksAndBudget: FC = () => {
    const clicks = useSelector(selectVacancyOfTheDayClicks);
    const budget = useSelector(selectVacancyOfTheDayBudget);
    const errors = useSelector(selectVacancyOfTheDayErrors);
    const presets = useSelector(selectVacancyOfTheDayPresets);

    const dispatch = useDispatch();
    const trls = useTrls();
    const { isMobile } = useBreakpoint();
    const { params } = useVacancyOfTheDayAnalyticsParams();

    const sendFieldTouch = (fieldName: 'clicks' | 'budget', nextClicks: number, nextBudget: number) => {
        vacancyOfTheDayFormFieldTouch({
            ...params,
            fieldName,
            preset: 'CUSTOM',
            budget: nextBudget,
            clicks: nextClicks,
            impressions: getVacancyOfTheDayForecast(nextClicks).impressions,
            geoIds: null,
            geoSource: 'None',
            roleIds: null,
            roleSource: 'None',
        });
    };

    const applyValues = (nextClicks: number, nextBudget: number) => {
        dispatch([
            setVacancyOfTheDayClicks(nextClicks),
            setVacancyOfTheDayBudget(nextBudget),
            setVacancyOfTheDayErrors({
                clicks: nextClicks < presets.SMALL.clicks,
                budget: nextBudget < presets.SMALL.budget,
            }),
        ]);
    };

    const handleClicksChange = (value: string) => {
        const nextClicks = Math.max(0, Number(value));
        applyValues(nextClicks, budgetFromClicks(nextClicks));
    };

    const handleBudgetChange = (value: string) => {
        const nextBudget = Math.max(0, Number(value));
        applyValues(clicksFromBudget(nextBudget), nextBudget);
    };

    return (
        <>
            <VSpacing default={16} />
            <Flex direction={isMobile ? 'column' : 'row'} gap={24}>
                <Flex direction="column" gap={12} grow={1} basis={0}>
                    <Text>{trls(TrlKeys.clicks)}</Text>
                    <Flex direction="row" gap={12} align="start">
                        <NumberInput
                            errorMessage={errors.clicks ? trls(TrlKeys.clicksError, [presets.SMALL.clicks]) : null}
                            invalid={errors.clicks}
                            value={String(clicks)}
                            onChange={handleClicksChange}
                            onBlur={() => sendFieldTouch('clicks', clicks, budget)}
                            onFocus={() => dispatch(setVacancyOfTheDayErrors({ clicks: false, budget: false }))}
                            groupSeparator={SPACE}
                            decimalLength={0}
                            allowNegative={false}
                            data-qa="vacancy-of-the-day-clicks-input"
                        />
                        <Flex direction="row" gap={8}>
                            <Button
                                mode="secondary"
                                style="neutral"
                                size="medium"
                                hideLabel
                                icon={<MinusOutlinedSize24 />}
                                onClick={() => {
                                    const nextClicks = Math.max(0, clicks - CLICKS_INPUT_STEP);
                                    handleClicksChange(String(nextClicks));
                                    sendFieldTouch('clicks', nextClicks, budgetFromClicks(nextClicks));
                                }}
                                data-qa="vacancy-of-the-day-clicks-decrease"
                            />
                            <Button
                                mode="secondary"
                                style="neutral"
                                size="medium"
                                hideLabel
                                icon={<PlusOutlinedSize24 />}
                                onClick={() => {
                                    const nextClicks = clicks + CLICKS_INPUT_STEP;
                                    handleClicksChange(String(nextClicks));
                                    sendFieldTouch('clicks', nextClicks, budgetFromClicks(nextClicks));
                                }}
                                data-qa="vacancy-of-the-day-clicks-increase"
                            />
                        </Flex>
                    </Flex>
                </Flex>
                <Flex direction="column" gap={12} grow={1} basis={0}>
                    <Text>{trls(TrlKeys.budget)}</Text>
                    <NumberInput
                        errorMessage={
                            errors.budget
                                ? trls(TrlKeys.budgetError, [formatCost(presets.SMALL.budget, NON_BREAKING_SPACE)])
                                : null
                        }
                        invalid={errors.budget}
                        value={String(budget)}
                        onChange={handleBudgetChange}
                        onFocus={() => dispatch(setVacancyOfTheDayErrors({ clicks: false, budget: false }))}
                        onBlur={() => {
                            const nextClicks = clicksFromBudget(budget);
                            const nextBudget = budgetFromClicks(nextClicks);
                            applyValues(nextClicks, nextBudget);
                            sendFieldTouch('budget', nextClicks, nextBudget);
                        }}
                        groupSeparator={SPACE}
                        decimalLength={0}
                        allowNegative={false}
                        postfix={trls(TrlKeys.postfix)}
                        data-qa="vacancy-of-the-day-budget-input"
                    />
                </Flex>
            </Flex>
        </>
    );
};
